export default {
  id: "payments",
  title: "Payments, Invoices & QuickBooks",
  section: "Billing & Reports",
  order: 52,
  keywords: "payments reconciliation warrant invoice quickbooks send to office billing email sequence letter remittance",
  body: `
## What it is

Three connected pieces close the billing loop:

1. **Invoices** are generated per authorization per month from approved
   shifts.
2. They're **sent** — into QuickBooks for accounting, or emailed to a state
   office for Job Development.
3. **Payment Tracking** reconciles what the state actually paid against
   what was billed.

## How invoicing works

- An invoice covers **one authorization for one month**. Open the
  authorization's details, pick the month, and generate — the app builds a
  filled invoice PDF from that month's approved shifts and files it on the
  client's documents.
- Every invoice gets a **sequence letter A through P** appended to the
  authorization number (D1000999**A**, D1000999**B**...). The letter is
  chosen automatically by checking QuickBooks for what already exists, so
  numbers never collide.
- Codes with invoicing disabled (the "create invoice" setting on the
  service code) and Job Development types don't generate QuickBooks
  invoices — JD bills through **Send to Office** instead.

### Step by step: invoice a VR authorization month

1. Open the authorization (from the Authorizations page or the client).
2. Generate the invoice for the month. Review the PDF.
3. **Send to QuickBooks.** The app uploads the invoice with its
   attachments — the authorization PDF and the month's USOR form — and
   records the QuickBooks invoice number.
4. If it's **Job Coaching (USOR 95)**: sending is blocked until the form's
   "client hours worked" is filled in and is at least the coached hours —
   fill that first.
5. If QuickBooks reports a duplicate, the app tells you which letters exist
   and what the next one is.

### Step by step: Job Development (Send to Office)

1. Generate/verify the USOR 96 for the month.
2. Use **Send to Office** — the authorization PDF and the 96 are emailed to
   the office's **billing email**.
3. The app refuses to double-send the same auth/month and shows when and to
   whom it went.

### Prerequisites that make sends fail

The chain must be complete: the authorization has a **counselor**, the
counselor has an **office**, and the office has a **billing email**. Any
missing link stops the send with an error naming it. The QuickBooks
connection itself is established once by an administrator signing into
QuickBooks; if a send reports the connection expired, it needs that
sign-in renewed.

## How Payment Tracking works

The Payments page is a reconciliation ledger: one record per client + code +
period with billed amount, paid amount, warrant number, and a computed
status — **Paid**, **Partial**, or **Unpaid**. Summary cards across the top
show Total Billed, Total Paid, Outstanding, and the **Match Rate**.

### Step by step: reconciling a remittance

1. Pick the period (month arrows or custom range).
2. **Upload CSV** with the remittance file — the preview shows the first
   rows, then the import reports created / skipped (duplicates) / errors
   per row. Or use **Create Payment** to enter a record by hand (billed
   amount auto-computes from rate × units if left blank).
3. Filter Status to **Unpaid / Partial** — that's your follow-up list.
4. When a payment arrives later, use **Adjust** on the record to set the
   paid amount; the status recalculates.
5. **Download CSV** for the current view any time you need to share the
   reconciliation state.
`,
};
